import React, { useEffect, useRef, useState } from "react";
import { getRollForwardPolicies, updateRollForwardPolicy } from "../../api";

const LABELS = {
  personal: "Personal",
  corporate: "Corporate",
  tslips: "T-Slips",
  other: "Other",
};

const ELIGIBILITY_LABELS = {
  period_reached: "When the period is reached",
  prior_complete: "When the prior period is complete",
  both: "Both",
  either: "Either",
};

const DUE_RULE_LABELS = {
  from_personal_type: "From personal type (Apr 30 / Jun 15)",
  months_after_year_end: "Months after year end",
  fixed_month_day: "Fixed month and day",
  from_recurrence: "From the task recurrence",
  none: "No due date",
};

const card = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
};

const field = {
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "6px 8px",
  fontSize: 13,
};

// Server-side validation ranges, mirrored here only as a fast, local guard
// against obvious typos. The server (which enforces due_month 1-12, due_day
// 1-31, due_months_after_year_end >= 0) remains the authority -- this just
// keeps the common mistake from making a round trip.
const RANGE_VALIDATORS = {
  due_month: (v) =>
    v == null || (v >= 1 && v <= 12) ? null : "Due month must be between 1 and 12.",
  due_day: (v) =>
    v == null || (v >= 1 && v <= 31) ? null : "Due day must be between 1 and 31.",
  due_months_after_year_end: (v) =>
    v == null || v >= 0 ? null : "Months after year end must be 0 or more.",
};

export default function RollForwardSettings() {
  const [policies, setPolicies] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [saveErr, setSaveErr] = useState("");
  // The last policy state confirmed by the server for each category, keyed
  // on category. Used to revert a field that was optimistically updated
  // (via setLocal) if the save that followed it is rejected or blocked by
  // the client-side guard, so nothing invalid is left looking saved.
  const lastGoodRef = useRef({});

  useEffect(() => {
    getRollForwardPolicies()
      .then((data) => {
        setPolicies(data);
        lastGoodRef.current = Object.fromEntries(data.map((p) => [p.category, p]));
      })
      .catch(() => setLoadErr("Could not load roll-forward settings."));
  }, []);

  const revertToLastGood = (category) => {
    const good = lastGoodRef.current[category];
    if (good) {
      setPolicies((prev) => prev.map((p) => (p.category === category ? good : p)));
    }
  };

  // Policy is stored with a signed lead_days; the UI splits it into a
  // magnitude and a before/after direction, because "-90" is not something a
  // manager should have to reason about.
  const save = async (category, patch) => {
    try {
      const updated = await updateRollForwardPolicy(category, patch);
      setPolicies((prev) =>
        prev.map((p) => (p.category === category ? updated : p)),
      );
      lastGoodRef.current[category] = updated;
      setSaveErr("");
    } catch (e) {
      // A failed save must not blank the whole section -- the cards (and
      // whatever the manager was in the middle of looking at) stay put. The
      // field that triggered the failed save is reverted to the server's
      // last known value, since it may have already been updated
      // optimistically by setLocal before this save fired.
      setSaveErr(e?.response?.data?.detail || "Could not save that change.");
      revertToLastGood(category);
    }
  };

  // Runs the cheap client-side range guard before ever calling the server.
  // On failure it shows the error and reverts the optimistic edit locally,
  // without making a request.
  const saveIfValid = (category, patch) => {
    for (const [key, validate] of Object.entries(RANGE_VALIDATORS)) {
      if (key in patch) {
        const message = validate(patch[key]);
        if (message) {
          setSaveErr(message);
          revertToLastGood(category);
          return;
        }
      }
    }
    save(category, patch);
  };

  const setLocal = (category, patch) =>
    setPolicies((prev) =>
      prev.map((p) => (p.category === category ? { ...p, ...patch } : p)),
    );

  if (loadErr) {
    return <p style={{ color: "#b91c1c", fontSize: 13 }}>{loadErr}</p>;
  }

  return (
    <div>
      {saveErr && (
        <p role="alert" style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>
          {saveErr}
        </p>
      )}
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
      {policies.map((p) => {
        const magnitude = Math.abs(p.lead_days ?? 0);
        // A lead_days of exactly 0 is deliberately treated as "before" (zero
        // days before the year end), not "after" -- the magnitude is 0
        // either way, so the direction choice here is cosmetic only.
        const direction = (p.lead_days ?? 0) <= 0 ? "before" : "after";
        return (
          <div key={p.category} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                {LABELS[p.category]}
              </h3>
              <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  aria-label={`Enable ${p.category}`}
                  checked={!!p.is_enabled}
                  onChange={(e) => save(p.category, { is_enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>

            <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
              Ready when
            </label>
            <select
              style={{ ...field, width: "100%", marginBottom: 12 }}
              value={p.eligibility}
              onChange={(e) => save(p.category, { eligibility: e.target.value })}
            >
              {Object.entries(ELIGIBILITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <label
              htmlFor={`lead_days-${p.category}`}
              style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
            >
              Ready this many days
            </label>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              <input
                id={`lead_days-${p.category}`}
                name={`lead_days-${p.category}`}
                type="number"
                min="0"
                style={{ ...field, width: 80 }}
                value={magnitude}
                onChange={(e) =>
                  setLocal(p.category, {
                    lead_days: direction === "before"
                      ? -Math.abs(Number(e.target.value))
                      : Math.abs(Number(e.target.value)),
                  })
                }
                onBlur={() => saveIfValid(p.category, { lead_days: p.lead_days })}
              />
              <select
                style={{ ...field, flex: 1 }}
                value={direction}
                onChange={(e) =>
                  save(p.category, {
                    lead_days: e.target.value === "before" ? -magnitude : magnitude,
                  })
                }
              >
                <option value="before">before</option>
                <option value="after">after</option>
              </select>
            </div>
            <p style={{ fontSize: 11, color: "#9ca3af", marginTop: -8, marginBottom: 12 }}>
              the year end{p.category === "other" ? " (due date for Other tasks)" : ""}
            </p>

            <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
              Due date
            </label>
            <select
              style={{ ...field, width: "100%" }}
              value={p.due_date_rule}
              onChange={(e) => save(p.category, { due_date_rule: e.target.value })}
            >
              {Object.entries(DUE_RULE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            {p.due_date_rule === "months_after_year_end" && (
              <input
                type="number"
                min="0"
                aria-label={`Months after year end for ${p.category}`}
                style={{ ...field, width: "100%", marginTop: 8 }}
                value={p.due_months_after_year_end ?? ""}
                onChange={(e) =>
                  setLocal(p.category, {
                    due_months_after_year_end: Number(e.target.value),
                  })
                }
                onBlur={() =>
                  saveIfValid(p.category, {
                    due_months_after_year_end: p.due_months_after_year_end,
                  })
                }
              />
            )}

            {p.due_date_rule === "fixed_month_day" && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input
                  type="number" min="1" max="12"
                  aria-label={`Due month for ${p.category}`}
                  style={{ ...field, width: "50%" }}
                  value={p.due_month ?? ""}
                  onChange={(e) => setLocal(p.category, { due_month: Number(e.target.value) })}
                  onBlur={() => saveIfValid(p.category, { due_month: p.due_month })}
                />
                <input
                  type="number" min="1" max="31"
                  aria-label={`Due day for ${p.category}`}
                  style={{ ...field, width: "50%" }}
                  value={p.due_day ?? ""}
                  onChange={(e) => setLocal(p.category, { due_day: Number(e.target.value) })}
                  onBlur={() => saveIfValid(p.category, { due_day: p.due_day })}
                />
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
